/**
 * modes/floating.ts — floating action button (FAB) mode.
 *
 * Creates a fixed-position button in the corner of the viewport.
 * Clicking it opens a popup (reuses popup mode internally).
 *
 * The trigger element (usually an invisible <div data-mp-mode="floating">)
 * is hidden — the FAB replaces it as the visual affordance.
 *
 * One FAB per page — if multiple floating triggers exist with the same org,
 * only the first one is mounted (subsequent ones are ignored with a warning).
 */

import { EmbedConfig } from '../config'
import { el } from '../dom'
import { injectStyles } from '../styles'
import { getOrCreatePopup } from './popup'

let fabMounted = false

export function mountFloating(trigger: HTMLElement, config: EmbedConfig): () => void {
  if (fabMounted) {
    console.warn('[ManagePatients] Multiple floating triggers found — only the first will be mounted.')
    trigger.style.display = 'none'
    return () => {}
  }
  fabMounted = true

  injectStyles()
  trigger.style.display = 'none'

  // Derive FAB colour: data-mp-color → org default (#0ea5e9)
  const color = config.color ?? '#0ea5e9'

  // Calendar icon SVG (inline, no external dependency)
  const iconSvg = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`

  const fab = el('button', {
    class: `mp-fab mp-fab-${config.position}`,
    type:  'button',
    'aria-label': config.label,
    style: `--mp-fab-color: ${color}`,
  })
  fab.innerHTML = iconSvg + `<span>${config.label}</span>`

  document.body.appendChild(fab)

  const popup = getOrCreatePopup(config)

  function handleClick(): void {
    popup.open()
  }

  fab.addEventListener('click', handleClick)

  return () => {
    fab.removeEventListener('click', handleClick)
    fab.remove()
    fabMounted = false
  }
}
